/* ============================================================
   TRANSFACTOR LOGISTICS - BILINGUAL CONTENT (EN / RO)
   Positioning: a small, dedicated, technology-enhanced freight
   forwarder. We organize and coordinate door-to-door transport
   through a vetted network of specialist carriers - one point of
   contact, one clear all-in price, documents handled for you.
   No invented team, no invented statistics, no implied assets.
   ============================================================ */

export const translations = {
  en: {
    nav: {
      home: "Home",
      services: "Services",
      resources: "Resources",
      about: "About",
      contact: "Contact",
      getQuote: "Get a Quote",
      getFreeQuote: "Get a Free Quote",
      viewAllServices: "View All Services",
      incoterms: "Incoterms 2020 Guide",
      importGuide: "First Import Guide",
      faq: "FAQ",
      blog: "Blog",
      calculator: "Shipping Calculator",
      home_aria: "Transfactor Logistics Home",
    },
    footer: {
      tagline:
        "A small, dedicated freight forwarder. We organize your door-to-door transport end to end - one point of contact, one clear price.",
      servicesHeading: "Services",
      companyHeading: "Company",
      resourcesHeading: "Resources",
      contactHeading: "Contact",
      aboutUs: "About Us",
      blog: "Blog",
      contact: "Contact",
      hoursWeekday: "Mon–Fri: 9:00–18:00",
      hoursNote: "Quotes answered within one business day",
      rights: "All rights reserved.",
      location: "Bucharest, Romania",
      email: "contact@transfactor-logistics.com",
    },
    common: {
      learnMore: "Learn More",
      requestQuote: "Request a Quote",
      contactUs: "Contact Us",
      getFreeQuote: "Get a Free Quote",
      ourServices: "Our Services",
      keyFeatures: "Key Features",
      howItWorks: "How It Works",
      faqHeading: "Frequently Asked Questions",
      readyTitle: "Ready to get started?",
      readyText:
        "Tell us about your shipment and we'll send a clear, all-in quote within one business day.",
    },
    home: {
      heroEyebrow: "Freight Forwarding",
      heroTitle: "Your shipment,",
      heroTitleAccent: "handled end to end",
      heroText:
        "We organize door-to-door transport for importers and exporters - sea, road and more - through a trusted network of specialist carriers. One point of contact, one clear price, every document taken care of.",
      heroCtaPrimary: "Get a Free Quote",
      heroCtaSecondary: "Our Services",
      servicesEyebrow: "What We Do",
      servicesTitle: "Logistics, organized around you",
      servicesText:
        "From ocean freight through Constanța to road transport across Europe, we coordinate every link in your supply chain so you don't have to.",
      viewAll: "View all services",
      whyEyebrow: "Why Work With Us",
      whyTitle: "Small team, full attention, modern tools",
      whyText:
        "Big forwarders treat smaller shippers as an afterthought. We don't. You get a direct line to the people running your shipment, backed by technology that keeps everything visible and on schedule.",
      stat1Value: "1",
      stat1Label: "Point of contact for the whole chain",
      stat2Value: "24h",
      stat2Label: "Typical quote turnaround",
      values: [
        {
          title: "One point of contact",
          description:
            "One team coordinates the whole journey - pickup, customs, main carriage and final delivery - so nothing falls between providers.",
        },
        {
          title: "Clear, all-in pricing",
          description:
            "A single total price per shipment, agreed up front. No surprise surcharges discovered along the way.",
        },
        {
          title: "Documents done right",
          description:
            "We coordinate bills of lading, CMR, customs declarations and proofs of origin so they're correct and on time.",
        },
        {
          title: "Visibility at every step",
          description:
            "Every shipment gets a unique reference and a status you can follow - proactive updates, not silence.",
        },
      ],
      ctaTitle: "Ready to move your next shipment?",
      ctaText:
        "Get in touch for a free, no-obligation quote. We'll show you the simplest way to get your cargo where it needs to be.",
      ctaButton: "Request a Free Quote",
    },
    about: {
      heroEyebrow: "About Us",
      heroTitle: "Logistics made simple",
      heroText:
        "We're a freight forwarder built for importers and exporters who want their shipments handled properly - without the overhead and indifference of the big networks.",
      missionTitle: "What we do",
      missionText1:
        "Transfactor Logistics organizes door-to-door transport on your behalf. We don't run our own trucks, ships or warehouses - instead we buy each link from specialist providers we trust and sell you one integrated service, with a single point of contact and a clear total price.",
      missionText2:
        "That keeps us flexible and focused. We choose the right carrier for each route rather than filling our own capacity, and we stay close enough to every shipment to catch problems before they reach you.",
      approachEyebrow: "How We're Different",
      approachTitle: "Small, dedicated, technology-enhanced",
      approachText:
        "We deliberately stay lean. A small, hands-on team means your shipment is run by people who know it by name - supported by modern tools that keep documents, milestones and pricing transparent from quote to delivery.",
      approach: [
        {
          title: "A small, dedicated team",
          description:
            "You deal directly with the people coordinating your cargo. Fast answers, real ownership, no call-centre runaround.",
        },
        {
          title: "Technology-enhanced",
          description:
            "We use modern tooling for tracking, documentation and pricing - so you get clarity and visibility usually reserved for much larger accounts.",
        },
        {
          title: "Asset-light and flexible",
          description:
            "Because we're not tied to our own fleet, we pick the best carrier for each shipment and route - optimized for your cargo, not our utilization.",
        },
        {
          title: "Founded on real freight experience",
          description:
            "Our work is grounded in hands-on experience with ocean freight through Constanța and road transport across Europe.",
        },
      ],
      focusTitle: "Who we work with",
      focusText:
        "We're built for stable small and mid-sized importers and exporters - businesses moving roughly 1–20 containers or 1–10 trucks a month. Big enough for a real relationship, too important to be treated as just another file.",
      ctaTitle: "Let's move your cargo",
      ctaText:
        "Tell us where it's going. We'll handle the rest and keep you in the loop the whole way.",
      ctaButton: "Get Started",
    },
    contact: {
      heroEyebrow: "Contact Us",
      heroTitle: "Let's talk logistics",
      heroText:
        "Have a shipment in mind or a question about a route? Send us the details and we'll get back to you within one business day.",
      formName: "Full Name",
      formNamePlaceholder: "Your name",
      formEmail: "Email Address",
      formEmailPlaceholder: "you@company.com",
      formCompany: "Company",
      formCompanyPlaceholder: "Your company",
      formService: "Service of Interest",
      formServiceSelect: "Select a service",
      formServiceOther: "Other",
      formMessage: "Message",
      formMessagePlaceholder: "Tell us about your shipment or question...",
      formSubmit: "Send Message",
      infoAddressTitle: "Location",
      infoAddress: "Bucharest, Romania",
      infoEmailTitle: "Email",
      infoEmail: "contact@transfactor-logistics.com",
      infoHoursTitle: "Business Hours",
      infoHours: "Mon–Fri: 9:00–18:00",
      infoResponseTitle: "Response Time",
      infoResponse: "Within one business day",
    },
    quote: {
      heroEyebrow: "Get a Quote",
      heroTitle: "Request a free quote",
      heroText:
        "Tell us about your shipment and we'll send a clear, all-in quote within one business day. No obligation, no hidden surcharges.",
      shipmentDetails: "Shipment Details",
      shipmentType: "Shipment Type",
      selectType: "Select type",
      import: "Import",
      export: "Export",
      crossTrade: "Cross-Trade",
      transportMode: "Transport Mode",
      selectMode: "Select mode",
      incotermsLabel: "Incoterms",
      selectIncoterm: "Select Incoterm (optional)",
      primaryService: "Primary Service",
      selectService: "Select service (optional)",
      routeInfo: "Route Information",
      origin: "Origin (City / Country)",
      originPlaceholder: "e.g. Shanghai, China",
      destination: "Destination (City / Country)",
      destinationPlaceholder: "e.g. Bucharest, Romania",
      cargoDetails: "Cargo Details",
      weight: "Gross Weight (kg)",
      volume: "Volume (m³)",
      pieces: "Number of Pieces",
      cargoDescription: "Cargo Description",
      cargoDescriptionPlaceholder:
        "Describe your cargo (type of goods, any special handling requirements...)",
      additionalServices: "Additional Services",
      contactInfo: "Your Contact Information",
      fullName: "Full Name",
      emailAddress: "Email Address",
      company: "Company",
      phone: "Phone Number",
      additionalNotes: "Additional Notes",
      additionalNotesPlaceholder: "Any other details or questions...",
      submit: "Submit Quote Request",
      whyTitle: "Why request a quote?",
      whyPoints: [
        "Free, no-obligation quotes",
        "Answered within one business day",
        "One clear, all-in price",
        "Tailored to your specific shipment",
        "Honest advice from people who know the route",
      ],
      talkTitle: "Prefer to talk?",
      talkText: "Send us an email and we'll arrange a call about your shipment.",
      talkEmail: "contact@transfactor-logistics.com",
      talkHours: "Mon–Fri, 9:00–18:00",
      calcHint: "Not sure of your volume or chargeable weight?",
      calcLink: "Use the shipping calculator",
    },
    calculator: {
      heroEyebrow: "Tools",
      heroTitle: "Shipping calculator",
      heroText:
        "Work out the volume, chargeable weight and container space for your shipment in seconds - then turn it into a real quote.",
      modeLabel: "Transport mode",
      modes: {
        sea: "Sea (LCL)",
        air: "Air",
        road: "Road (groupage)",
        rail: "Rail",
      },
      unitsLabel: "Units",
      metric: "Metric (cm / kg)",
      imperial: "Imperial (in / lb)",
      cargoHeading: "Cargo dimensions",
      cargoHint:
        "Enter the size and weight of each package type. Add a row for every different package.",
      colLength: "Length",
      colWidth: "Width",
      colHeight: "Height",
      colWeight: "Weight / unit",
      colQty: "Qty",
      addRow: "Add package",
      removeRow: "Remove package",
      resultsHeading: "Your estimate",
      totalVolume: "Total volume",
      grossWeight: "Gross weight",
      volumetricWeight: "Volumetric weight",
      chargeableWeight: "Chargeable weight",
      chargeableNote:
        "Carriers bill the greater of actual and volumetric weight.",
      emptyState: "Enter at least one package to see your estimate.",
      containerHeading: "Container fit",
      containerHint:
        "Approximate, based on practical loadable space. Real fit depends on stacking and packaging.",
      colContainer: "Container",
      colFill: "Fill (one unit)",
      colNeeded: "Units needed",
      transitHeading: "Typical transit time",
      transitHint:
        "Indicative ranges on our core lanes. Actual time depends on schedules, customs and routing.",
      transitRoute: "Route",
      transitTime: "Transit",
      noTransit: "Select a mode to see indicative transit times.",
      prefillCta: "Get an exact quote",
      prefillNote: "We'll carry these figures into the quote form.",
      disclaimer:
        "Estimates only - not a binding quote. Volumetric factors: air 1:6 (167 kg/m³), road 1:3 (333 kg/m³), sea & rail 1 t/m³.",
    },
    calculatorTransit: [
      { mode: "sea", route: "Asia → Constanța", time: "30–40 days" },
      { mode: "sea", route: "Constanța → inland Romania", time: "1–3 days" },
      { mode: "rail", route: "China → Europe", time: "16–20 days" },
      { mode: "road", route: "Romania ↔ Western Europe", time: "2–5 days" },
      { mode: "road", route: "Romania ↔ Central Europe", time: "1–3 days" },
      { mode: "air", route: "Worldwide (airport to airport)", time: "1–5 days" },
    ],
    services: {
      heroEyebrow: "Our Services",
      heroTitle: "Everything your shipment needs",
      heroText:
        "From ocean and road freight to customs, insurance and warehousing, we coordinate the full chain - buying each link from trusted specialists and delivering it to you as one service.",
      ctaTitle: "Need a tailored solution?",
      ctaText:
        "Every shipment is different. Tell us your route and cargo, and we'll put together the right combination for you.",
      categoryDescriptions: {
        Transport:
          "Door-to-door freight by sea and road, with air and rail arranged on request - coordinated through carriers we know and trust.",
        "Customs & Trade":
          "Customs clearance through licensed brokers, fiscal representation and cargo insurance to keep your trade compliant and protected.",
        Warehousing:
          "Storage and bonded warehousing arranged through partner facilities, with inventory handling and distribution.",
      },
    },
    importGuide: {
      heroEyebrow: "Resources",
      heroTitle: "First import guide",
      heroText:
        "A step-by-step guide for businesses importing for the first time - from finding a supplier to receiving your cargo.",
      introText1:
        "Importing for the first time can feel overwhelming: suppliers to vet, trade terms to understand, transport to arrange, documents to prepare and customs to navigate. This guide walks you through the essentials.",
      introText2:
        "If you'd rather have it handled, that's exactly what we do - we'll guide you at every stage and coordinate the whole shipment for you.",
      keyTips: "Key Tips",
      ctaTitle: "Ready to start importing?",
      ctaText:
        "Let us handle the logistics while you focus on your business. Get a free quote and straightforward guidance for your first import.",
      ctaButtonSecondary: "Talk to Us",
    },
    notFound: {
      title: "Page not found",
      text:
        "Looks like this shipment got lost in transit. Let's get you back on track.",
      button: "Back to Homepage",
    },
    faqPage: {
      heroEyebrow: "FAQ",
      heroTitle: "Frequently asked questions",
      heroText:
        "Answers to common questions about how we organize freight, handle customs, insure cargo and arrange storage.",
      cantFindTitle: "Can't find your answer?",
      cantFindText:
        "We're happy to help with any question about your shipment or a specific route.",
      newToImportTitle: "New to importing?",
      newToImportText:
        "Read our step-by-step guide for first-time importers.",
      stillTitle: "Still have questions?",
      stillText:
        "We're happy to help. Get in touch and we'll get back to you within one business day.",
    },
    faqItems: [
      {
        question: "What does a freight forwarder actually do?",
        answer:
          "We organize and coordinate your transport from door to door without operating the trucks or ships ourselves. We buy each link - pickup, main carriage, customs, final delivery - from specialist providers and sell you one integrated service with a single point of contact and one clear price.",
      },
      {
        question: "What types of freight do you handle?",
        answer:
          "Our core strengths are ocean freight (FCL and LCL, especially through Constanța) and road freight between Romania and the rest of Europe. We also arrange air and rail freight on request, and we coordinate customs, cargo insurance and warehousing through trusted partners.",
      },
      {
        question: "How can I follow my shipment?",
        answer:
          "Every shipment gets a unique reference and a status you can follow. We keep you updated proactively at the key milestones - departure, transshipment, arrival and delivery - rather than leaving you to chase for news.",
      },
      {
        question: "Do you handle international shipping and customs?",
        answer:
          "Yes. We organize international freight on the routes we know best and coordinate customs clearance through licensed customs brokers, so your declarations, duties and documentation are handled correctly.",
      },
      {
        question: "How do I get a quote?",
        answer:
          "Use our online quote form or email us at contact@transfactor-logistics.com. Tell us the route, the cargo and your Incoterm, and we'll send a clear, all-in quote within one business day.",
      },
      {
        question: "How is your pricing structured?",
        answer:
          "You get one total price per shipment, agreed up front - not a base rate followed by surcharges discovered along the way. If anything about your shipment changes the cost, we tell you before it happens.",
      },
      {
        question: "Who do you work best with?",
        answer:
          "Stable small and mid-sized importers and exporters - typically moving around 1–20 containers or 1–10 trucks a month. Large enough for a real working relationship, and important enough to get our full attention on every file.",
      },
      {
        question: "What is bonded warehousing?",
        answer:
          "A bonded warehouse is a customs-supervised facility where imported goods can be stored without paying import duties or VAT. Duties become payable only when the goods are released into free circulation, and goods that are re-exported never incur domestic duties. We arrange this through partner facilities.",
      },
      {
        question: "What documents do I need for customs clearance?",
        answer:
          "Typically a commercial invoice, packing list, bill of lading or air waybill, and a customs declaration. Depending on the goods you may also need a certificate of origin or specific permits. We collect and check these with you and coordinate the declaration through a licensed broker.",
      },
      {
        question: "What is fiscal customs representation?",
        answer:
          "It lets you defer VAT on non-EU imports. Instead of paying VAT at the border, it's reported on your periodic VAT return where it can be deducted - neutralizing the cash impact and freeing up working capital.",
      },
      {
        question: "Do I need cargo insurance?",
        answer:
          "Carrier liability is capped by international conventions and is usually far below the real value of your goods. Cargo insurance covers the full commercial value against loss, damage and theft. We can arrange suitable cover alongside your shipment.",
      },
      {
        question: "What are Incoterms and why do they matter?",
        answer:
          "Incoterms are internationally recognized rules that define who is responsible for transport, insurance and customs, and at what point risk passes from seller to buyer. The Incoterm you agree decides who pays for the main carriage - and therefore who hires us. Choosing the right one shapes your costs and risk on every shipment.",
      },
    ],
    seo: {
      siteDescription:
        "A small, dedicated freight forwarder. We organize door-to-door transport for importers and exporters - one point of contact, one clear price.",
      home: {
        title: "Freight Forwarder in Romania",
        description:
          "Transfactor Logistics - a small, dedicated freight forwarder organizing door-to-door sea and road transport for importers and exporters. One point of contact, one clear price.",
      },
      about: {
        title: "About",
        description:
          "A small, dedicated, technology-enhanced freight forwarder. Learn how we organize door-to-door transport through a trusted network of specialist carriers.",
      },
      services: {
        title: "Services",
        description:
          "Door-to-door sea and road freight, customs clearance, cargo insurance and warehousing - coordinated end to end through trusted specialist partners.",
      },
      contact: {
        title: "Contact",
        description:
          "Get in touch with Transfactor Logistics for a free quote or to discuss your shipment. We reply within one business day.",
      },
      quote: {
        title: "Request a Quote",
        description:
          "Request a free, no-obligation quote. Tell us about your shipment and we'll send a clear, all-in price within one business day.",
      },
      faq: {
        title: "FAQ",
        description:
          "Answers to common questions about how Transfactor Logistics organizes freight, handles customs, insures cargo and arranges storage.",
      },
      importGuide: {
        title: "First Import Guide",
        description:
          "Step-by-step guide for first-time importers: find suppliers, choose Incoterms, arrange transport, prepare documents and clear customs.",
      },
      calculator: {
        title: "Shipping Calculator",
        description:
          "Free shipping calculator: work out CBM volume, chargeable weight and container space for sea, air, road and rail - then request an exact quote.",
      },
      incoterms: {
        title: "Incoterms 2020 Guide",
        description:
          "Complete reference to all 11 Incoterms 2020 rules. Understand seller and buyer responsibilities, risk transfer, and when to use each term.",
      },
      notFound: {
        title: "Page Not Found",
        description: "The page you're looking for doesn't exist.",
      },
    },
    importSteps: [
      {
        number: "01",
        title: "Find a reliable supplier",
        description:
          "Research and select a supplier that meets your quality, price and reliability requirements. Verify their credentials, request samples and agree commercial terms before placing orders.",
        tips: [
          "Use trade platforms or trade shows to find and compare suppliers",
          "Always request product samples before committing to large orders",
          "Verify supplier credentials through business registration checks",
          "Agree payment terms that protect both parties",
        ],
      },
      {
        number: "02",
        title: "Understand Incoterms",
        description:
          "Incoterms define who is responsible for transport, insurance and customs at each stage. The term you agree decides who pays for the main carriage - and shapes your costs and risk.",
        tips: [
          "FOB and CIF are the most common terms for sea freight",
          "FCA is preferred for containerized and multimodal shipments",
          "DDP gives you a fully landed cost but relies on the supplier's logistics",
          "Always specify the Incoterm and named place in your purchase contract",
        ],
        linkText: "View our Incoterms 2020 guide",
        linkTo: "/incoterms",
      },
      {
        number: "03",
        title: "Choose your transport mode",
        description:
          "Select the mode that fits your cargo, volume, budget and timeline. Each one trades off cost, speed and capacity differently - we'll help you weigh them.",
        tips: [
          "Ocean freight: most economical for larger volumes",
          "Air freight: fastest, best for urgent or high-value goods",
          "Rail freight: a balanced middle ground on the China–Europe corridor",
          "Road freight: most flexible for European distribution",
        ],
        linkText: "Explore our transport services",
        linkTo: "/services",
      },
      {
        number: "04",
        title: "Prepare your documentation",
        description:
          "International imports need specific documents for customs clearance. Missing or incorrect paperwork is the most common cause of delays - so we collect and check it with you.",
        tips: [
          "Commercial invoice: describes the goods, their value and the parties",
          "Packing list: details contents, weight and dimensions per package",
          "Bill of lading / air waybill: the carrier's receipt and contract of carriage",
          "Certificate of origin: may be needed for preferential duty rates",
          "Extra permits may apply to regulated goods (food, chemicals, electronics)",
        ],
      },
      {
        number: "05",
        title: "Customs clearance",
        description:
          "All imported goods must be declared, classified under the correct tariff code, and have applicable duties and VAT settled before release. We coordinate this through licensed brokers.",
        tips: [
          "Work with a licensed customs broker to avoid costly errors",
          "Get the HS classification right - mistakes can lead to penalties",
          "Advance clearance can reduce delays on arrival",
          "Consider fiscal representation for VAT deferral on larger imports",
        ],
        linkText: "Learn about our customs brokerage service",
        linkTo: "/services/customs-brokerage",
      },
      {
        number: "06",
        title: "Receive and inspect your goods",
        description:
          "Once clearance is complete we arrange delivery to your premises or warehouse. Inspect the goods on arrival and document any damage or discrepancy immediately.",
        tips: [
          "Inspect goods on arrival and note any damage on the delivery receipt",
          "Report damage to the carrier and your insurer within the required window",
          "Keep records of all received goods for inventory and accounting",
          "Consider warehousing if you need storage before final distribution",
        ],
        linkText: "Explore our warehousing solutions",
        linkTo: "/services/warehousing",
      },
    ],
    incotermsPage: {
      heroEyebrow: "Resources",
      heroTitle: "Incoterms 2020 Guide",
      heroText:
        "A comprehensive reference to all 11 Incoterms 2020 rules, explaining responsibilities, risk transfer, and when to use each term.",
      introP1:
        "Incoterms (International Commercial Terms) are a set of internationally recognized rules published by the International Chamber of Commerce (ICC) that define the responsibilities of sellers and buyers in international trade transactions. They specify who is responsible for shipping, insurance, documentation, customs clearance, and at what point risk transfers from seller to buyer.",
      introP2a: "The current version, ",
      introP2Bold: "Incoterms 2020",
      introP2b:
        ", includes 11 rules divided into four groups. Seven rules apply to any mode of transport, while four are specific to sea and inland waterway transport.",
      groupsTitle: "The Four Groups",
      quickRefTitle: "Quick Reference",
      tableCode: "Code",
      tableName: "Name",
      tableGroup: "Group",
      tableModes: "Modes",
      allTitle: "All 11 Incoterms Explained",
      sellerResp: "Seller Responsibilities",
      buyerResp: "Buyer Responsibilities",
      bestUsed: "Best used when:",
      applicableTo: "Applicable to:",
      ctaTitle: "Need Help Choosing the Right Incoterm?",
      ctaText:
        "Our trade specialists can advise you on the best Incoterm for your specific shipment. Get in touch for expert guidance.",
      ctaQuote: "Get a Quote",
      ctaCustoms: "Customs Brokerage",
    },
    incotermGroups: [
      { code: "E", name: "Departure", description: "Seller makes goods available at their premises" },
      { code: "F", name: "Main Carriage Unpaid", description: "Seller delivers to a carrier nominated by the buyer" },
      { code: "C", name: "Main Carriage Paid", description: "Seller arranges and pays for the main carriage" },
      { code: "D", name: "Arrival", description: "Seller bears all costs and risks to the destination" },
    ],
    incoterms: [
      {
        code: "EXW",
        name: "Ex Works",
        group: "E",
        modes: "Any",
        definition:
          "The seller makes the goods available at their premises. The buyer bears all costs and risks of transporting the goods from the seller's location to the destination.",
        sellerResponsibilities: [
          "Make goods available at seller's premises",
          "Provide commercial invoice and documentation",
        ],
        buyerResponsibilities: [
          "All transport costs from seller's premises",
          "Export and import clearance",
          "Loading at seller's premises",
          "All risks from point of collection",
        ],
        useCase:
          "Suitable when the buyer has strong logistics capabilities and wants full control over the supply chain.",
      },
      {
        code: "FCA",
        name: "Free Carrier",
        group: "F",
        modes: "Any",
        definition:
          "The seller delivers the goods to the carrier or another person nominated by the buyer at the seller's premises or a named place. Risk transfers when goods are handed to the carrier.",
        sellerResponsibilities: [
          "Deliver goods to carrier at named place",
          "Export clearance",
          "Loading if delivery is at seller's premises",
        ],
        buyerResponsibilities: [
          "Main carriage costs",
          "Import clearance and duties",
          "Risk from delivery to carrier",
        ],
        useCase:
          "One of the most versatile terms. Ideal for containerized shipments and multimodal transport.",
      },
      {
        code: "CPT",
        name: "Carriage Paid To",
        group: "C",
        modes: "Any",
        definition:
          "The seller pays freight to the named destination, but risk transfers to the buyer when goods are handed to the first carrier.",
        sellerResponsibilities: [
          "Arrange and pay for carriage to destination",
          "Export clearance",
          "Deliver goods to first carrier",
        ],
        buyerResponsibilities: [
          "Risk from first carrier handover",
          "Import clearance and duties",
          "Unloading at destination",
        ],
        useCase:
          "Useful when the seller can negotiate better freight rates but wants risk to transfer early.",
      },
      {
        code: "CIP",
        name: "Carriage and Insurance Paid To",
        group: "C",
        modes: "Any",
        definition:
          "Like CPT, but the seller must also arrange insurance covering the buyer's risk during carriage. Requires minimum Institute Cargo Clauses (A) coverage.",
        sellerResponsibilities: [
          "Arrange and pay for carriage to destination",
          "Insurance (minimum ICC-A coverage)",
          "Export clearance",
        ],
        buyerResponsibilities: [
          "Risk from first carrier handover",
          "Import clearance and duties",
          "Unloading at destination",
        ],
        useCase:
          "Preferred when both transport and insurance should be arranged by the seller, with broader coverage than CIF.",
      },
      {
        code: "DAP",
        name: "Delivered at Place",
        group: "D",
        modes: "Any",
        definition:
          "The seller delivers when goods are placed at the buyer's disposal at the named destination, ready for unloading. Seller bears all risks and costs to that point.",
        sellerResponsibilities: [
          "All transport to named destination",
          "Export clearance",
          "All risks until goods arrive at destination",
        ],
        buyerResponsibilities: [
          "Unloading at destination",
          "Import clearance and duties",
        ],
        useCase:
          "Ideal when the seller manages logistics to the buyer's door, but the buyer handles import customs.",
      },
      {
        code: "DPU",
        name: "Delivered at Place Unloaded",
        group: "D",
        modes: "Any",
        definition:
          "The seller delivers and unloads the goods at the named destination. The only Incoterm requiring the seller to unload goods.",
        sellerResponsibilities: [
          "All transport to named destination",
          "Unloading at destination",
          "Export clearance",
          "All risks until goods are unloaded",
        ],
        buyerResponsibilities: [
          "Import clearance and duties",
          "Further transport from place of unloading",
        ],
        useCase:
          "Suitable when the seller has the capability and willingness to unload at destination (formerly DAT).",
      },
      {
        code: "DDP",
        name: "Delivered Duty Paid",
        group: "D",
        modes: "Any",
        definition:
          "The seller delivers goods cleared for import, with all duties and taxes paid, at the named destination. Maximum obligation for the seller.",
        sellerResponsibilities: [
          "All transport to destination",
          "Export and import clearance",
          "All duties, taxes, and customs charges",
          "All risks until delivery",
        ],
        buyerResponsibilities: [
          "Unloading at destination (unless agreed otherwise)",
        ],
        useCase:
          "Best for buyers who want a fully landed cost with no surprises. The seller must be registered for customs in the destination country.",
      },
      {
        code: "FAS",
        name: "Free Alongside Ship",
        group: "F",
        modes: "Sea & Inland Waterway",
        definition:
          "The seller delivers when goods are placed alongside the vessel at the named port. Risk transfers at that point.",
        sellerResponsibilities: [
          "Deliver goods alongside vessel at port",
          "Export clearance",
        ],
        buyerResponsibilities: [
          "Loading onto vessel",
          "Main carriage (ocean freight)",
          "Import clearance and duties",
          "Risk from alongside vessel",
        ],
        useCase:
          "Used for bulk cargo or non-containerized shipments at sea ports.",
      },
      {
        code: "FOB",
        name: "Free on Board",
        group: "F",
        modes: "Sea & Inland Waterway",
        definition:
          "The seller delivers goods on board the vessel at the named port. Risk transfers once goods are loaded on the ship.",
        sellerResponsibilities: [
          "Deliver goods on board the vessel",
          "Export clearance",
          "Loading costs at port of shipment",
        ],
        buyerResponsibilities: [
          "Main carriage (ocean freight)",
          "Import clearance and duties",
          "Risk from on board vessel",
        ],
        useCase:
          "One of the most commonly used sea freight terms. Ideal when the buyer arranges ocean freight.",
      },
      {
        code: "CFR",
        name: "Cost and Freight",
        group: "C",
        modes: "Sea & Inland Waterway",
        definition:
          "The seller pays costs and freight to the named destination port. However, risk transfers to the buyer once goods are loaded on the vessel at the port of shipment.",
        sellerResponsibilities: [
          "Arrange and pay ocean freight to destination port",
          "Export clearance",
          "Loading at port of shipment",
        ],
        buyerResponsibilities: [
          "Risk from on board vessel at origin",
          "Import clearance and duties",
          "Unloading at destination port",
        ],
        useCase:
          "Common for sea freight when the seller negotiates the ocean freight rate.",
      },
      {
        code: "CIF",
        name: "Cost, Insurance and Freight",
        group: "C",
        modes: "Sea & Inland Waterway",
        definition:
          "Like CFR, but the seller also provides marine insurance. Minimum coverage is Institute Cargo Clauses (C), which is narrower than CIP.",
        sellerResponsibilities: [
          "Arrange and pay ocean freight to destination port",
          "Marine insurance (minimum ICC-C)",
          "Export clearance",
        ],
        buyerResponsibilities: [
          "Risk from on board vessel at origin",
          "Import clearance and duties",
          "Unloading at destination port",
        ],
        useCase:
          "Very common in international sea trade. Buyer should consider additional insurance for higher-value cargo.",
      },
    ],
  },

  ro: {
    nav: {
      home: "Acasă",
      services: "Servicii",
      resources: "Resurse",
      about: "Despre noi",
      contact: "Contact",
      getQuote: "Cere o ofertă",
      getFreeQuote: "Cere o ofertă gratuită",
      viewAllServices: "Vezi toate serviciile",
      incoterms: "Ghid Incoterms 2020",
      importGuide: "Ghid pentru primul import",
      faq: "Întrebări frecvente",
      blog: "Blog",
      calculator: "Calculator de transport",
      home_aria: "Transfactor Logistics - Acasă",
    },
    footer: {
      tagline:
        "O casă de expediții mică și dedicată. Organizăm transportul tău door-to-door de la cap la coadă - un singur punct de contact, un preț clar.",
      servicesHeading: "Servicii",
      companyHeading: "Companie",
      resourcesHeading: "Resurse",
      contactHeading: "Contact",
      aboutUs: "Despre noi",
      blog: "Blog",
      contact: "Contact",
      hoursWeekday: "Luni–Vineri: 9:00–18:00",
      hoursNote: "Răspundem la cereri de ofertă într-o zi lucrătoare",
      rights: "Toate drepturile rezervate.",
      location: "București, România",
      email: "contact@transfactor-logistics.com",
    },
    common: {
      learnMore: "Află mai multe",
      requestQuote: "Cere o ofertă",
      contactUs: "Contactează-ne",
      getFreeQuote: "Cere o ofertă gratuită",
      ourServices: "Serviciile noastre",
      keyFeatures: "Caracteristici principale",
      howItWorks: "Cum funcționează",
      faqHeading: "Întrebări frecvente",
      readyTitle: "Gata de început?",
      readyText:
        "Spune-ne detaliile expedierii și îți trimitem o ofertă clară, totul inclus, într-o zi lucrătoare.",
    },
    home: {
      heroEyebrow: "Casă de expediții",
      heroTitle: "Expedierea ta,",
      heroTitleAccent: "gestionată de la cap la coadă",
      heroText:
        "Organizăm transport door-to-door pentru importatori și exportatori - maritim, rutier și nu numai - printr-o rețea de transportatori specializați de încredere. Un singur punct de contact, un preț clar și toate documentele rezolvate.",
      heroCtaPrimary: "Cere o ofertă gratuită",
      heroCtaSecondary: "Serviciile noastre",
      servicesEyebrow: "Ce facem",
      servicesTitle: "Logistică organizată în jurul tău",
      servicesText:
        "De la transport maritim prin Constanța până la rutier în toată Europa, coordonăm fiecare verigă din lanțul tău, ca tu să nu fie nevoie.",
      viewAll: "Vezi toate serviciile",
      whyEyebrow: "De ce noi",
      whyTitle: "Echipă mică, atenție totală, instrumente moderne",
      whyText:
        "Casele mari de expediții tratează clienții mici la urmă. Noi nu. Vorbești direct cu oamenii care îți gestionează expedierea, susținuți de tehnologie care ține totul vizibil și la timp.",
      stat1Value: "1",
      stat1Label: "Punct de contact pentru tot lanțul",
      stat2Value: "24h",
      stat2Label: "Timp obișnuit de răspuns la ofertă",
      values: [
        {
          title: "Un singur punct de contact",
          description:
            "O singură echipă coordonează tot drumul - preluare, vamă, transportul principal și livrarea finală - ca nimic să nu cadă între furnizori.",
        },
        {
          title: "Preț clar, totul inclus",
          description:
            "Un singur preț total per expediere, agreat din start. Fără suprataxe descoperite pe parcurs.",
        },
        {
          title: "Documente făcute corect",
          description:
            "Coordonăm conosamentul, CMR-ul, declarațiile vamale și dovezile de origine, ca să fie corecte și la timp.",
        },
        {
          title: "Vizibilitate la fiecare pas",
          description:
            "Fiecare expediere are o referință unică și o stare pe care o poți urmări - actualizări proactive, nu tăcere.",
        },
      ],
      ctaTitle: "Gata să expediezi următorul transport?",
      ctaText:
        "Contactează-ne pentru o ofertă gratuită, fără obligații. Îți arătăm cea mai simplă cale ca marfa ta să ajungă unde trebuie.",
      ctaButton: "Cere o ofertă gratuită",
    },
    about: {
      heroEyebrow: "Despre noi",
      heroTitle: "Logistică simplă",
      heroText:
        "Suntem o casă de expediții făcută pentru importatori și exportatori care vor ca expedierile lor să fie gestionate cum trebuie - fără birocrația și indiferența rețelelor mari.",
      missionTitle: "Ce facem",
      missionText1:
        "Transfactor Logistics organizează transport door-to-door în numele tău. Nu avem camioane, nave sau depozite proprii - cumpărăm fiecare verigă de la furnizori specializați de încredere și îți vindem un serviciu integrat, cu un singur punct de contact și un preț total clar.",
      missionText2:
        "Asta ne ține flexibili și concentrați. Alegem transportatorul potrivit pentru fiecare rută, în loc să ne umplem propria capacitate, și rămânem destul de aproape de fiecare expediere ca să prindem problemele înainte să ajungă la tine.",
      approachEyebrow: "Prin ce ne diferențiem",
      approachTitle: "Mici, dedicați, sprijiniți de tehnologie",
      approachText:
        "Rămânem deliberat subțiri. O echipă mică și implicată înseamnă că expedierea ta este gestionată de oameni care o cunosc pe nume - susținuți de instrumente moderne care țin documentele, etapele și prețul transparente de la ofertă la livrare.",
      approach: [
        {
          title: "O echipă mică și dedicată",
          description:
            "Discuți direct cu oamenii care îți coordonează marfa. Răspunsuri rapide, responsabilitate reală, fără call-center.",
        },
        {
          title: "Sprijiniți de tehnologie",
          description:
            "Folosim instrumente moderne pentru urmărire, documentație și prețuri - ca să ai claritatea și vizibilitatea rezervate de obicei conturilor mult mai mari.",
        },
        {
          title: "Fără active proprii, flexibili",
          description:
            "Pentru că nu suntem legați de o flotă proprie, alegem cel mai bun transportator pentru fiecare expediere și rută - optimizat pentru marfa ta, nu pentru gradul nostru de ocupare.",
        },
        {
          title: "Pornim de la experiență reală",
          description:
            "Munca noastră se sprijină pe experiență directă cu transportul maritim prin Constanța și cu rutierul în toată Europa.",
        },
      ],
      focusTitle: "Cu cine lucrăm",
      focusText:
        "Suntem făcuți pentru importatori și exportatori IMM stabili - firme care mută aproximativ 1–20 containere sau 1–10 camioane pe lună. Destul de mari pentru o relație reală, prea importanți ca să fie tratați ca un simplu dosar.",
      ctaTitle: "Hai să-ți mutăm marfa",
      ctaText:
        "Spune-ne unde merge. Ne ocupăm noi de rest și te ținem la curent tot drumul.",
      ctaButton: "Începe acum",
    },
    contact: {
      heroEyebrow: "Contact",
      heroTitle: "Hai să vorbim despre logistică",
      heroText:
        "Ai o expediere în plan sau o întrebare despre o rută? Trimite-ne detaliile și revenim într-o zi lucrătoare.",
      formName: "Nume complet",
      formNamePlaceholder: "Numele tău",
      formEmail: "Adresă de e-mail",
      formEmailPlaceholder: "tu@firma.ro",
      formCompany: "Companie",
      formCompanyPlaceholder: "Firma ta",
      formService: "Serviciul de interes",
      formServiceSelect: "Alege un serviciu",
      formServiceOther: "Altceva",
      formMessage: "Mesaj",
      formMessagePlaceholder: "Spune-ne despre expedierea sau întrebarea ta...",
      formSubmit: "Trimite mesajul",
      infoAddressTitle: "Locație",
      infoAddress: "București, România",
      infoEmailTitle: "E-mail",
      infoEmail: "contact@transfactor-logistics.com",
      infoHoursTitle: "Program",
      infoHours: "Luni–Vineri: 9:00–18:00",
      infoResponseTitle: "Timp de răspuns",
      infoResponse: "Într-o zi lucrătoare",
    },
    quote: {
      heroEyebrow: "Cere o ofertă",
      heroTitle: "Cere o ofertă gratuită",
      heroText:
        "Spune-ne detaliile expedierii și îți trimitem o ofertă clară, totul inclus, într-o zi lucrătoare. Fără obligații, fără suprataxe ascunse.",
      shipmentDetails: "Detalii expediere",
      shipmentType: "Tip expediere",
      selectType: "Alege tipul",
      import: "Import",
      export: "Export",
      crossTrade: "Cross-trade",
      transportMode: "Mod de transport",
      selectMode: "Alege modul",
      incotermsLabel: "Incoterms",
      selectIncoterm: "Alege Incoterm (opțional)",
      primaryService: "Serviciu principal",
      selectService: "Alege serviciul (opțional)",
      routeInfo: "Informații despre rută",
      origin: "Origine (oraș / țară)",
      originPlaceholder: "ex. Shanghai, China",
      destination: "Destinație (oraș / țară)",
      destinationPlaceholder: "ex. București, România",
      cargoDetails: "Detalii marfă",
      weight: "Greutate brută (kg)",
      volume: "Volum (m³)",
      pieces: "Număr de colete",
      cargoDescription: "Descrierea mărfii",
      cargoDescriptionPlaceholder:
        "Descrie marfa (tipul bunurilor, cerințe speciale de manipulare...)",
      additionalServices: "Servicii suplimentare",
      contactInfo: "Datele tale de contact",
      fullName: "Nume complet",
      emailAddress: "Adresă de e-mail",
      company: "Companie",
      phone: "Număr de telefon",
      additionalNotes: "Note suplimentare",
      additionalNotesPlaceholder: "Orice alte detalii sau întrebări...",
      submit: "Trimite cererea de ofertă",
      whyTitle: "De ce să ceri o ofertă?",
      whyPoints: [
        "Oferte gratuite, fără obligații",
        "Răspuns într-o zi lucrătoare",
        "Un singur preț clar, totul inclus",
        "Adaptată expedierii tale",
        "Sfaturi oneste de la oameni care cunosc ruta",
      ],
      talkTitle: "Preferi să vorbim?",
      talkText: "Trimite-ne un e-mail și aranjăm un apel despre expedierea ta.",
      talkEmail: "contact@transfactor-logistics.com",
      talkHours: "Luni–Vineri, 9:00–18:00",
      calcHint: "Nu știi volumul sau greutatea taxabilă?",
      calcLink: "Folosește calculatorul de transport",
    },
    calculator: {
      heroEyebrow: "Instrumente",
      heroTitle: "Calculator de transport",
      heroText:
        "Află în câteva secunde volumul, greutatea taxabilă și spațiul în container pentru expedierea ta - apoi transform-o într-o ofertă reală.",
      modeLabel: "Mod de transport",
      modes: {
        sea: "Maritim (LCL)",
        air: "Aerian",
        road: "Rutier (grupaj)",
        rail: "Feroviar",
      },
      unitsLabel: "Unități",
      metric: "Metric (cm / kg)",
      imperial: "Imperial (in / lb)",
      cargoHeading: "Dimensiunile mărfii",
      cargoHint:
        "Introdu dimensiunea și greutatea fiecărui tip de colet. Adaugă un rând pentru fiecare colet diferit.",
      colLength: "Lungime",
      colWidth: "Lățime",
      colHeight: "Înălțime",
      colWeight: "Greutate / buc.",
      colQty: "Cant.",
      addRow: "Adaugă colet",
      removeRow: "Șterge coletul",
      resultsHeading: "Estimarea ta",
      totalVolume: "Volum total",
      grossWeight: "Greutate brută",
      volumetricWeight: "Greutate volumetrică",
      chargeableWeight: "Greutate taxabilă",
      chargeableNote:
        "Transportatorii taxează cea mai mare dintre greutatea reală și cea volumetrică.",
      emptyState: "Introdu cel puțin un colet pentru a vedea estimarea.",
      containerHeading: "Încărcare în container",
      containerHint:
        "Aproximativ, în funcție de spațiul practic încărcabil. Încărcarea reală depinde de stivuire și ambalare.",
      colContainer: "Container",
      colFill: "Umplere (o unitate)",
      colNeeded: "Unități necesare",
      transitHeading: "Timp de tranzit orientativ",
      transitHint:
        "Intervale orientative pe rutele noastre principale. Timpul real depinde de orare, vamă și rutare.",
      transitRoute: "Rută",
      transitTime: "Tranzit",
      noTransit: "Alege un mod pentru a vedea timpii de tranzit orientativi.",
      prefillCta: "Cere o ofertă exactă",
      prefillNote: "Preluăm aceste valori în formularul de ofertă.",
      disclaimer:
        "Doar estimări - nu o ofertă fermă. Factori volumetrici: aerian 1:6 (167 kg/m³), rutier 1:3 (333 kg/m³), maritim și feroviar 1 t/m³.",
    },
    calculatorTransit: [
      { mode: "sea", route: "Asia → Constanța", time: "30–40 zile" },
      { mode: "sea", route: "Constanța → interiorul României", time: "1–3 zile" },
      { mode: "rail", route: "China → Europa", time: "16–20 zile" },
      { mode: "road", route: "România ↔ Europa de Vest", time: "2–5 zile" },
      { mode: "road", route: "România ↔ Europa Centrală", time: "1–3 zile" },
      { mode: "air", route: "Global (aeroport la aeroport)", time: "1–5 zile" },
    ],
    services: {
      heroEyebrow: "Serviciile noastre",
      heroTitle: "Tot ce are nevoie expedierea ta",
      heroText:
        "De la transport maritim și rutier până la vamă, asigurare și depozitare, coordonăm tot lanțul - cumpărând fiecare verigă de la specialiști de încredere și livrându-ți-o ca un singur serviciu.",
      ctaTitle: "Ai nevoie de o soluție pe măsură?",
      ctaText:
        "Fiecare expediere e diferită. Spune-ne ruta și marfa, iar noi punem la punct combinația potrivită pentru tine.",
      categoryDescriptions: {
        Transport:
          "Transport door-to-door maritim și rutier, cu aerian și feroviar la cerere - coordonat prin transportatori pe care îi cunoaștem și în care avem încredere.",
        "Customs & Trade":
          "Vămuire prin comisionari vamali autorizați, reprezentare fiscală și asigurare a mărfii, ca schimbul tău comercial să rămână conform și protejat.",
        Warehousing:
          "Depozitare și antrepozit vamal aranjate prin depozite partenere, cu gestiunea stocurilor și distribuție.",
      },
    },
    importGuide: {
      heroEyebrow: "Resurse",
      heroTitle: "Ghid pentru primul import",
      heroText:
        "Un ghid pas cu pas pentru firmele care importă pentru prima dată - de la găsirea unui furnizor până la primirea mărfii.",
      introText1:
        "Primul import poate părea copleșitor: furnizori de verificat, condiții comerciale de înțeles, transport de organizat, documente de pregătit și vamă de parcurs. Acest ghid te poartă prin esențiale.",
      introText2:
        "Dacă preferi să fie rezolvat, exact asta facem - te ghidăm la fiecare pas și coordonăm toată expedierea pentru tine.",
      keyTips: "Sfaturi cheie",
      ctaTitle: "Gata să începi să imporți?",
      ctaText:
        "Lasă logistica pe noi, ca tu să te concentrezi pe afacerea ta. Cere o ofertă gratuită și îndrumare clară pentru primul tău import.",
      ctaButtonSecondary: "Vorbește cu noi",
    },
    notFound: {
      title: "Pagina nu a fost găsită",
      text:
        "Se pare că această expediere s-a rătăcit pe drum. Hai să te punem înapoi pe traseu.",
      button: "Înapoi la pagina principală",
    },
    faqPage: {
      heroEyebrow: "Întrebări frecvente",
      heroTitle: "Întrebări frecvente",
      heroText:
        "Răspunsuri la întrebările obișnuite despre cum organizăm transportul, gestionăm vama, asigurăm marfa și aranjăm depozitarea.",
      cantFindTitle: "Nu găsești răspunsul?",
      cantFindText:
        "Te ajutăm cu plăcere cu orice întrebare despre expedierea ta sau o rută anume.",
      newToImportTitle: "Imporți pentru prima dată?",
      newToImportText:
        "Citește ghidul nostru pas cu pas pentru importatori la început de drum.",
      stillTitle: "Mai ai întrebări?",
      stillText:
        "Te ajutăm cu plăcere. Contactează-ne și revenim într-o zi lucrătoare.",
    },
    faqItems: [
      {
        question: "Ce face, de fapt, o casă de expediții?",
        answer:
          "Organizăm și coordonăm transportul tău de la ușă la ușă, fără să operăm noi camioanele sau navele. Cumpărăm fiecare verigă - preluare, transport principal, vamă, livrare finală - de la furnizori specializați și îți vindem un serviciu integrat, cu un singur punct de contact și un preț clar.",
      },
      {
        question: "Ce tipuri de transport gestionați?",
        answer:
          "Punctele noastre forte sunt transportul maritim (FCL și LCL, mai ales prin Constanța) și cel rutier între România și restul Europei. Aranjăm și aerian sau feroviar la cerere și coordonăm vama, asigurarea mărfii și depozitarea prin parteneri de încredere.",
      },
      {
        question: "Cum îmi pot urmări expedierea?",
        answer:
          "Fiecare expediere primește o referință unică și o stare pe care o poți urmări. Te ținem la curent proactiv la momentele-cheie - plecare, transbordare, sosire și livrare - nu te lăsăm să alergi după vești.",
      },
      {
        question: "Gestionați transport internațional și vamă?",
        answer:
          "Da. Organizăm transport internațional pe rutele pe care le cunoaștem cel mai bine și coordonăm vămuirea prin comisionari vamali autorizați, ca declarațiile, taxele și documentele să fie corecte.",
      },
      {
        question: "Cum obțin o ofertă?",
        answer:
          "Folosește formularul de ofertă online sau scrie-ne la contact@transfactor-logistics.com. Spune-ne ruta, marfa și Incoterm-ul, iar noi îți trimitem o ofertă clară, totul inclus, într-o zi lucrătoare.",
      },
      {
        question: "Cum este structurat prețul?",
        answer:
          "Primești un singur preț total per expediere, agreat din start - nu un tarif de bază urmat de suprataxe descoperite pe parcurs. Dacă ceva schimbă costul, îți spunem înainte să se întâmple.",
      },
      {
        question: "Cu cine lucrați cel mai bine?",
        answer:
          "Cu importatori și exportatori IMM stabili - de regulă cei care mută în jur de 1–20 containere sau 1–10 camioane pe lună. Destul de mari pentru o relație reală și suficient de importanți ca să primească toată atenția noastră la fiecare dosar.",
      },
      {
        question: "Ce este antrepozitul vamal?",
        answer:
          "Un antrepozit vamal este un spațiu sub supraveghere vamală unde bunurile importate pot fi depozitate fără a plăti taxe vamale sau TVA. Taxele devin exigibile abia la eliberarea în liberă circulație, iar marfa reexportată nu suportă niciodată taxe interne. Aranjăm asta prin depozite partenere.",
      },
      {
        question: "Ce documente îmi trebuie pentru vămuire?",
        answer:
          "De regulă factura comercială, lista de ambalare (packing list), conosamentul sau scrisoarea de transport aerian și o declarație vamală. În funcție de marfă, pot fi necesare și un certificat de origine sau permise specifice. Le colectăm și le verificăm împreună cu tine și coordonăm declarația printr-un comisionar autorizat.",
      },
      {
        question: "Ce este reprezentarea fiscală vamală?",
        answer:
          "Îți permite să amâni TVA-ul la importurile extra-UE. În loc să plătești TVA la graniță, acesta se raportează în decontul periodic de TVA, unde poate fi dedus - neutralizând impactul asupra numerarului și eliberând capital de lucru.",
      },
      {
        question: "Am nevoie de asigurarea mărfii?",
        answer:
          "Răspunderea cărăușului este plafonată de convențiile internaționale și, de obicei, mult sub valoarea reală a bunurilor. Asigurarea mărfii acoperă valoarea comercială integrală împotriva pierderii, deteriorării și furtului. Putem aranja o acoperire potrivită odată cu expedierea.",
      },
      {
        question: "Ce sunt Incoterms și de ce contează?",
        answer:
          "Incoterms sunt reguli recunoscute internațional care stabilesc cine răspunde de transport, asigurare și vamă și în ce moment trece riscul de la vânzător la cumpărător. Incoterm-ul agreat decide cine plătește transportul principal - și deci cine ne angajează. Alegerea corectă îți modelează costurile și riscul la fiecare expediere.",
      },
    ],
    seo: {
      siteDescription:
        "O casă de expediții mică și dedicată. Organizăm transport door-to-door pentru importatori și exportatori - un singur punct de contact, un preț clar.",
      home: {
        title: "Casă de expediții în România",
        description:
          "Transfactor Logistics - o casă de expediții mică și dedicată, care organizează transport maritim și rutier door-to-door pentru importatori și exportatori. Un singur punct de contact, un preț clar.",
      },
      about: {
        title: "Despre noi",
        description:
          "O casă de expediții mică, dedicată și sprijinită de tehnologie. Află cum organizăm transport door-to-door printr-o rețea de transportatori specializați de încredere.",
      },
      services: {
        title: "Servicii",
        description:
          "Transport maritim și rutier door-to-door, vămuire, asigurarea mărfii și depozitare - coordonate de la cap la coadă prin parteneri specializați de încredere.",
      },
      contact: {
        title: "Contact",
        description:
          "Contactează Transfactor Logistics pentru o ofertă gratuită sau ca să discutăm expedierea ta. Răspundem într-o zi lucrătoare.",
      },
      quote: {
        title: "Cere o ofertă",
        description:
          "Cere o ofertă gratuită, fără obligații. Spune-ne detaliile expedierii și îți trimitem un preț clar, totul inclus, într-o zi lucrătoare.",
      },
      faq: {
        title: "Întrebări frecvente",
        description:
          "Răspunsuri la întrebările obișnuite despre cum organizează Transfactor Logistics transportul, gestionează vama, asigură marfa și aranjează depozitarea.",
      },
      importGuide: {
        title: "Ghid pentru primul import",
        description:
          "Ghid pas cu pas pentru primul import: găsește furnizori, alege Incoterms, organizează transportul, pregătește documentele și vămuiește marfa.",
      },
      calculator: {
        title: "Calculator de transport",
        description:
          "Calculator gratuit de transport: află volumul CBM, greutatea taxabilă și spațiul în container pentru maritim, aerian, rutier și feroviar - apoi cere o ofertă exactă.",
      },
      incoterms: {
        title: "Ghid Incoterms 2020",
        description:
          "Referință completă a tuturor celor 11 reguli Incoterms 2020. Înțelege responsabilitățile vânzătorului și cumpărătorului, transferul riscului și când se folosește fiecare termen.",
      },
      notFound: {
        title: "Pagina nu a fost găsită",
        description: "Pagina pe care o cauți nu există.",
      },
    },
    importSteps: [
      {
        number: "01",
        title: "Găsește un furnizor de încredere",
        description:
          "Caută și alege un furnizor care îți îndeplinește cerințele de calitate, preț și fiabilitate. Verifică-i acreditivările, cere mostre și agreează condițiile comerciale înainte de a plasa comenzi.",
        tips: [
          "Folosește platforme comerciale sau târguri ca să găsești și să compari furnizori",
          "Cere mereu mostre înainte de comenzi mari",
          "Verifică acreditivările furnizorului prin registrul comerțului",
          "Agreează termene de plată care protejează ambele părți",
        ],
      },
      {
        number: "02",
        title: "Înțelege Incoterms",
        description:
          "Incoterms stabilesc cine răspunde de transport, asigurare și vamă la fiecare etapă. Termenul agreat decide cine plătește transportul principal - și îți modelează costurile și riscul.",
        tips: [
          "FOB și CIF sunt cele mai uzuale condiții pentru maritim",
          "FCA este preferat pentru expedieri containerizate și multimodale",
          "DDP îți dă un cost complet la destinație, dar depinde de logistica furnizorului",
          "Specifică mereu Incoterm-ul și locul convenit în contractul de cumpărare",
        ],
        linkText: "Vezi ghidul nostru Incoterms 2020",
        linkTo: "/incoterms",
      },
      {
        number: "03",
        title: "Alege modul de transport",
        description:
          "Alege modul potrivit pentru marfa, volumul, bugetul și termenul tău. Fiecare echilibrează diferit costul, viteza și capacitatea - te ajutăm să le cântărești.",
        tips: [
          "Maritim: cel mai economic pentru volume mari",
          "Aerian: cel mai rapid, ideal pentru marfă urgentă sau de valoare",
          "Feroviar: un echilibru pe coridorul China–Europa",
          "Rutier: cel mai flexibil pentru distribuție europeană",
        ],
        linkText: "Vezi serviciile noastre de transport",
        linkTo: "/services",
      },
      {
        number: "04",
        title: "Pregătește documentele",
        description:
          "Importurile internaționale cer documente specifice pentru vămuire. Documentele lipsă sau greșite sunt cea mai frecventă cauză a întârzierilor - de aceea le colectăm și le verificăm împreună cu tine.",
        tips: [
          "Factura comercială: descrie marfa, valoarea și părțile",
          "Packing list: detaliază conținutul, greutatea și dimensiunile per colet",
          "Conosament / scrisoare de transport aerian: dovada și contractul de transport",
          "Certificat de origine: poate fi necesar pentru taxe preferăriale",
          "Pot fi necesare permise suplimentare pentru bunuri reglementate (alimente, chimicale, electronice)",
        ],
      },
      {
        number: "05",
        title: "Vămuirea",
        description:
          "Toate bunurile importate trebuie declarate, clasificate la codul tarifar corect și să li se achite taxele și TVA înainte de eliberare. Coordonăm asta prin comisionari autorizați.",
        tips: [
          "Lucrează cu un comisionar vamal autorizat ca să eviți erori costisitoare",
          "Clasifică corect codul tarifar (HS) - greșelile pot aduce penalități",
          "Vămuirea în avans poate reduce întârzierile la sosire",
          "Ia în calcul reprezentarea fiscală pentru amânarea TVA la importuri mari",
        ],
        linkText: "Află despre serviciul de comisionariat vamal",
        linkTo: "/services/customs-brokerage",
      },
      {
        number: "06",
        title: "Primește și inspectează marfa",
        description:
          "După finalizarea vămuirii aranjăm livrarea la sediul sau depozitul tău. Inspectează marfa la sosire și documentează imediat orice avarie sau neconcordanță.",
        tips: [
          "Inspectează marfa la sosire și notează orice avarie pe avizul de livrare",
          "Raportează avariile către cărăuș și asigurător în termenul cerut",
          "Păstrează evidența bunurilor primite pentru stoc și contabilitate",
          "Ia în calcul depozitarea dacă ai nevoie de stocare înainte de distribuție",
        ],
        linkText: "Vezi soluțiile noastre de depozitare",
        linkTo: "/services/warehousing",
      },
    ],
    incotermsPage: {
      heroEyebrow: "Resurse",
      heroTitle: "Ghid Incoterms 2020",
      heroText:
        "O referință completă a tuturor celor 11 reguli Incoterms 2020, care explică responsabilitățile, transferul riscului și momentul în care se folosește fiecare termen.",
      introP1:
        "Incoterms (International Commercial Terms) sunt un set de reguli recunoscute la nivel internațional, publicate de Camera Internațională de Comerț (ICC), care definesc responsabilitățile vânzătorilor și cumpărătorilor în tranzacțiile comerciale internaționale. Acestea precizează cine răspunde de transport, asigurare, documentație și vămuire și în ce moment se transferă riscul de la vânzător la cumpărător.",
      introP2a: "Versiunea actuală, ",
      introP2Bold: "Incoterms 2020",
      introP2b:
        ", cuprinde 11 reguli împărțite în patru grupe. Șapte reguli se aplică oricărui mod de transport, iar patru sunt specifice transportului maritim și pe căile navigabile interioare.",
      groupsTitle: "Cele patru grupe",
      quickRefTitle: "Referință rapidă",
      tableCode: "Cod",
      tableName: "Denumire",
      tableGroup: "Grupă",
      tableModes: "Moduri",
      allTitle: "Toate cele 11 Incoterms explicate",
      sellerResp: "Responsabilitățile vânzătorului",
      buyerResp: "Responsabilitățile cumpărătorului",
      bestUsed: "Recomandat când:",
      applicableTo: "Aplicabil pentru:",
      ctaTitle: "Ai nevoie de ajutor în alegerea Incoterm-ului potrivit?",
      ctaText:
        "Specialiștii noștri în comerț te pot consilia cu privire la cel mai potrivit Incoterm pentru expedierea ta. Contactează-ne pentru îndrumare de specialitate.",
      ctaQuote: "Cere o ofertă",
      ctaCustoms: "Comisionariat vamal",
    },
    incotermGroups: [
      { code: "E", name: "Plecare", description: "Vânzătorul pune marfa la dispoziție la sediul său" },
      { code: "F", name: "Transport principal neplătit", description: "Vânzătorul livrează către un transportator desemnat de cumpărător" },
      { code: "C", name: "Transport principal plătit", description: "Vânzătorul organizează și plătește transportul principal" },
      { code: "D", name: "Sosire", description: "Vânzătorul suportă toate costurile și riscurile până la destinație" },
    ],
    incoterms: [
      {
        code: "EXW",
        name: "Franco fabrică",
        group: "E",
        modes: "Oricare",
        definition:
          "Vânzătorul pune marfa la dispoziție la sediul său. Cumpărătorul suportă toate costurile și riscurile transportului mărfii de la locația vânzătorului până la destinație.",
        sellerResponsibilities: [
          "Pune marfa la dispoziție la sediul vânzătorului",
          "Furnizează factura comercială și documentele",
        ],
        buyerResponsibilities: [
          "Toate costurile de transport de la sediul vânzătorului",
          "Vămuirea la export și la import",
          "Încărcarea la sediul vânzătorului",
          "Toate riscurile din momentul preluării",
        ],
        useCase:
          "Potrivit când cumpărătorul are capacități logistice solide și vrea control deplin asupra lanțului de aprovizionare.",
      },
      {
        code: "FCA",
        name: "Franco transportator",
        group: "F",
        modes: "Oricare",
        definition:
          "Vânzătorul livrează marfa către transportatorul sau persoana desemnată de cumpărător, la sediul vânzătorului sau într-un loc convenit. Riscul se transferă la predarea mărfii către transportator.",
        sellerResponsibilities: [
          "Livrează marfa către transportator în locul convenit",
          "Vămuirea la export",
          "Încărcarea, dacă livrarea are loc la sediul vânzătorului",
        ],
        buyerResponsibilities: [
          "Costurile transportului principal",
          "Vămuirea la import și taxele vamale",
          "Riscul de la predarea către transportator",
        ],
        useCase:
          "Unul dintre cei mai versatili termeni. Ideal pentru expedieri containerizate și transport multimodal.",
      },
      {
        code: "CPT",
        name: "Transport plătit până la",
        group: "C",
        modes: "Oricare",
        definition:
          "Vânzătorul plătește transportul până la destinația convenită, însă riscul se transferă cumpărătorului la predarea mărfii către primul transportator.",
        sellerResponsibilities: [
          "Organizează și plătește transportul până la destinație",
          "Vămuirea la export",
          "Predă marfa primului transportator",
        ],
        buyerResponsibilities: [
          "Riscul de la predarea către primul transportator",
          "Vămuirea la import și taxele vamale",
          "Descărcarea la destinație",
        ],
        useCase:
          "Util când vânzătorul poate negocia tarife de transport mai bune, dar vrea ca riscul să se transfere devreme.",
      },
      {
        code: "CIP",
        name: "Transport și asigurare plătite până la",
        group: "C",
        modes: "Oricare",
        definition:
          "La fel ca CPT, dar vânzătorul trebuie să asigure și acoperirea riscului cumpărătorului pe durata transportului. Necesită acoperire minimă conform Institute Cargo Clauses (A).",
        sellerResponsibilities: [
          "Organizează și plătește transportul până la destinație",
          "Asigurare (acoperire minimă ICC-A)",
          "Vămuirea la export",
        ],
        buyerResponsibilities: [
          "Riscul de la predarea către primul transportator",
          "Vămuirea la import și taxele vamale",
          "Descărcarea la destinație",
        ],
        useCase:
          "Preferat când atât transportul, cât și asigurarea trebuie aranjate de vânzător, cu acoperire mai largă decât CIF.",
      },
      {
        code: "DAP",
        name: "Livrat la locul convenit",
        group: "D",
        modes: "Oricare",
        definition:
          "Vânzătorul livrează când marfa este pusă la dispoziția cumpărătorului la destinația convenită, gata de descărcare. Vânzătorul suportă toate riscurile și costurile până în acel punct.",
        sellerResponsibilities: [
          "Tot transportul până la destinația convenită",
          "Vămuirea la export",
          "Toate riscurile până când marfa ajunge la destinație",
        ],
        buyerResponsibilities: [
          "Descărcarea la destinație",
          "Vămuirea la import și taxele vamale",
        ],
        useCase:
          "Ideal când vânzătorul gestionează logistica până la ușa cumpărătorului, dar cumpărătorul se ocupă de vama la import.",
      },
      {
        code: "DPU",
        name: "Livrat la locul convenit, descărcat",
        group: "D",
        modes: "Oricare",
        definition:
          "Vânzătorul livrează și descarcă marfa la destinația convenită. Singurul Incoterm care obligă vânzătorul să descarce marfa.",
        sellerResponsibilities: [
          "Tot transportul până la destinația convenită",
          "Descărcarea la destinație",
          "Vămuirea la export",
          "Toate riscurile până la descărcarea mărfii",
        ],
        buyerResponsibilities: [
          "Vămuirea la import și taxele vamale",
          "Transportul mai departe de la locul descărcării",
        ],
        useCase:
          "Potrivit când vânzătorul are capacitatea și disponibilitatea de a descărca la destinație (fostul DAT).",
      },
      {
        code: "DDP",
        name: "Livrat cu taxe vamale plătite",
        group: "D",
        modes: "Oricare",
        definition:
          "Vânzătorul livrează marfa vămuită la import, cu toate taxele vamale și impozitele plătite, la destinația convenită. Obligație maximă pentru vânzător.",
        sellerResponsibilities: [
          "Tot transportul până la destinație",
          "Vămuirea la export și la import",
          "Toate taxele vamale, impozitele și cheltuielile vamale",
          "Toate riscurile până la livrare",
        ],
        buyerResponsibilities: [
          "Descărcarea la destinație (dacă nu se convine altfel)",
        ],
        useCase:
          "Cel mai potrivit pentru cumpărătorii care vor un cost final complet, fără surprize. Vânzătorul trebuie să fie înregistrat vamal în țara de destinație.",
      },
      {
        code: "FAS",
        name: "Franco de-a lungul navei",
        group: "F",
        modes: "Maritim și căi navigabile interioare",
        definition:
          "Vânzătorul livrează când marfa este pusă de-a lungul navei în portul convenit. Riscul se transferă în acel moment.",
        sellerResponsibilities: [
          "Livrează marfa de-a lungul navei în port",
          "Vămuirea la export",
        ],
        buyerResponsibilities: [
          "Încărcarea la bordul navei",
          "Transportul principal (navlu maritim)",
          "Vămuirea la import și taxele vamale",
          "Riscul din momentul amplasării de-a lungul navei",
        ],
        useCase:
          "Folosit pentru marfă vrac sau expedieri necontainerizate în porturile maritime.",
      },
      {
        code: "FOB",
        name: "Franco la bord",
        group: "F",
        modes: "Maritim și căi navigabile interioare",
        definition:
          "Vânzătorul livrează marfa la bordul navei în portul convenit. Riscul se transferă odată ce marfa este încărcată pe navă.",
        sellerResponsibilities: [
          "Livrează marfa la bordul navei",
          "Vămuirea la export",
          "Costurile de încărcare în portul de expediere",
        ],
        buyerResponsibilities: [
          "Transportul principal (navlu maritim)",
          "Vămuirea la import și taxele vamale",
          "Riscul din momentul aflării la bordul navei",
        ],
        useCase:
          "Unul dintre cei mai folosiți termeni pentru transportul maritim. Ideal când cumpărătorul aranjează navlul maritim.",
      },
      {
        code: "CFR",
        name: "Cost și navlu",
        group: "C",
        modes: "Maritim și căi navigabile interioare",
        definition:
          "Vânzătorul plătește costurile și navlul până la portul de destinație convenit. Totuși, riscul se transferă cumpărătorului odată ce marfa este încărcată pe navă în portul de expediere.",
        sellerResponsibilities: [
          "Organizează și plătește navlul maritim până la portul de destinație",
          "Vămuirea la export",
          "Încărcarea în portul de expediere",
        ],
        buyerResponsibilities: [
          "Riscul din momentul aflării la bordul navei în origine",
          "Vămuirea la import și taxele vamale",
          "Descărcarea în portul de destinație",
        ],
        useCase:
          "Frecvent în transportul maritim când vânzătorul negociază tariful de navlu.",
      },
      {
        code: "CIF",
        name: "Cost, asigurare și navlu",
        group: "C",
        modes: "Maritim și căi navigabile interioare",
        definition:
          "La fel ca CFR, dar vânzătorul asigură și o poliță de asigurare maritimă. Acoperirea minimă este Institute Cargo Clauses (C), mai restrânsă decât la CIP.",
        sellerResponsibilities: [
          "Organizează și plătește navlul maritim până la portul de destinație",
          "Asigurare maritimă (minimum ICC-C)",
          "Vămuirea la export",
        ],
        buyerResponsibilities: [
          "Riscul din momentul aflării la bordul navei în origine",
          "Vămuirea la import și taxele vamale",
          "Descărcarea în portul de destinație",
        ],
        useCase:
          "Foarte frecvent în comerțul maritim internațional. Cumpărătorul ar trebui să ia în calcul o asigurare suplimentară pentru marfa de valoare mai mare.",
      },
    ],
  },
} as const

// Widen literal string/array types so EN and RO share one structural type.
type Widen<T> = T extends string
  ? string
  : T extends readonly (infer U)[]
    ? readonly Widen<U>[]
    : { readonly [K in keyof T]: Widen<T[K]> }

export type Dictionary = Widen<(typeof translations)["en"]>
